interface FeaturebaseCallbackData {
  action: 'widgetReady' | 'unreadChangelogsCountChanged'
  unreadCount?: number
}

type FeaturebaseCallback = (error: Error | null, data?: FeaturebaseCallbackData) => void

interface FeaturebaseBaseOptions {
  organization: string
  theme: 'light' | 'dark'
  locale?: string
  featurebaseJwt?: string
}

interface FeaturebaseChangelogOptions extends FeaturebaseBaseOptions {
  changelogCard?: {
    enabled: boolean
    layout?: {
      position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
      marginBottom?: number
      marginSide?: number
    }
  }
  dropdown?: {
    enabled: boolean
    placement?: 'bottom' | 'top' | 'left' | 'right' | 'auto'
  }
  popup?: {
    enabled: boolean
    usersName?: string
    autoOpenForNewUpdates?: boolean
  }
}

type Featurebase = (
  operation: 'init_changelog_widget',
  options: FeaturebaseChangelogOptions,
  callback?: FeaturebaseCallback
) => void

declare global {
  interface Window {
    Featurebase?: Featurebase
  }
}

interface ProvidedFeaturebase {
  isReady: boolean
  unreadChangelogCount: number
}

export default defineNuxtPlugin({
  name: 'featurebase',
  dependsOn: ['i18n'],
  setup() {
    const { featurebase: featurebaseSettings, mode } = useRuntimeConfig().public
    const { logError } = useErrorHandling()
    const { featurebaseRepository } = useApi()
    const { language } = storeToRefs(useLanguageStore())
    const userStore = useUserStore()
    const businessStore = useBusinessStore()
    const directSignStore = useDirectSignStore()
    const { isScalePlan } = useUserPlanInfo()

    const state = reactive<ProvidedFeaturebase>({
      isReady: false,
      unreadChangelogCount: 0,
    })

    // Validate configuration
    if (!featurebaseSettings?.organization || !featurebaseSettings?.scriptUrl) {
      if (mode === 'production' || mode === 'staging') {
        logError('Required Featurebase environment variables not provided', new Error())
      }

      return {
        provide: {
          featurebase: state,
        },
      }
    }

    let featurebaseInstance: Featurebase | null = null
    let ssoToken: string | undefined

    const getLocale = () => {
      // Convert 'en-gb' to 'en', 'de-ch' to 'de', etc.
      return language.value.split('-')[0]
    }

    const initChangelogWidget = () => {
      if (!featurebaseInstance) return

      featurebaseInstance(
        'init_changelog_widget',
        {
          organization: featurebaseSettings.organization,
          theme: 'light',
          locale: getLocale(),
          featurebaseJwt: ssoToken,
          changelogCard: {
            enabled: false,
          },
          dropdown: {
            enabled: true,
            placement: 'bottom',
          },
          popup: {
            enabled: true,
            usersName: userStore.firstName,
            autoOpenForNewUpdates: true,
          },
        },
        (error, data) => {
          if (error) {
            logError('Featurebase changelog widget error', error)
            return
          }

          if (data?.action === 'unreadChangelogsCountChanged') {
            state.unreadChangelogCount = data.unreadCount ?? 0
          }

          if (data?.action === 'widgetReady') {
            state.isReady = true
          }
        }
      )
    }

    const initializeWidgets = async () => {
      if (!featurebaseInstance) return

      try {
        const response = await featurebaseRepository.getSsoToken({
          name: `${userStore.firstName} ${userStore.lastName}`.trim() || userStore.email,
          email: userStore.email,
          userId: userStore.id,
          companies: businessStore.id ? [{ id: businessStore.id, name: businessStore.name }] : [],
        })
        ssoToken = response.token
      } catch (error) {
        logError('Failed to get Featurebase SSO token', error)
        // Continue without JWT - widget will work in anonymous mode
      }

      initChangelogWidget()
      state.isReady = true
    }

    // Trigger script loading when user is signed in (not DirectSign, not Scale plan)
    const shouldLoadScript = computed(() => {
      return Boolean(userStore.id && !directSignStore.isDirectSign && !isScalePlan.value)
    })

    const { status: scriptStatus, onError: onScriptError } = useScript(
      {
        src: featurebaseSettings.scriptUrl,
        async: true,
      },
      {
        trigger: shouldLoadScript,
      }
    )

    onScriptError(error => {
      logError('Loading Featurebase SDK script failed', error)
    })

    // Intercept when Featurebase is set on window
    // This must be set up before the script loads
    Object.defineProperty(window, 'Featurebase', {
      get() {
        return featurebaseInstance
      },
      set(value) {
        featurebaseInstance = value
      },
      configurable: true,
    })

    // Watch for script load and Featurebase instance
    const unwatchScript = watchEffect(() => {
      if (scriptStatus.value === 'loaded' && featurebaseInstance) {
        void initializeWidgets()
        unwatchScript()
      }
    })

    // Re-init changelog widget when language changes — the SDK replaces the widget on re-init
    watch(language, () => {
      if (state.isReady) {
        initChangelogWidget()
      }
    })

    return {
      provide: {
        featurebase: state,
      },
    }
  },
})
